import 'dayjs/locale/sv';
import 'react-photo-view/dist/react-photo-view.css';

import { message, Progress } from 'antd';
import React, { useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useDraftSubmit, useDraftUpdate } from '@/hooks/influencer';
import { useQueryClient } from '@tanstack/react-query';

import { ChevronDown, ChevronUp, Trash2, Download } from 'react-feather';
import { TextAreaAntd } from '@/components/text-area';
import { useUnifiedCloudinaryUpload, CloudinaryFile, FileWithMeta, ErrorType } from '@/hooks';
import { FileUploader } from '@/components/file-uploader';
import { MediaItem } from '@/components/media-item';
import { Button, Link } from '@/components';
import dayjs from 'dayjs';
import { Drawer } from '@/components/drawer/Drawer';
import { processField } from '@/utils/textUtils';
import { getMediaThumbnailUrl, getVideoPosterUrl } from '@/utils/mediaUtils';
import { DraftDataMedia } from '@/hooks/influencer/useDraftUpdate';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import type { OverlayRenderProps } from 'react-photo-view/dist/types';
import styles from './CollaborationItem.module.css';

import CheckIcon from '../../../../../public/assets/icons/check.svg';
import MoreIcon from '../../../../../public/assets/icons/more.svg';
import MoreBlueIcon from '../../../../../public/assets/icons/not_review.svg';
import { DraftHistoryItem } from './DraftHistoryItem';
import { DraftFeedbackModal, FeedbackType } from '../modals/feedback-modal/DraftFeedbackModalModal';

interface DraftProps {
  draft: Api.Influencer.Draft;
  setStatus: (code: string) => void;
  setSuccess: () => void;
}

interface FormType {
  medias: DraftMedia[];
  caption: string;
  permalink: string;
}

interface BadgeType {
  class: string;
  label: string;
}

const badgeStyles: Record<Api.Influencer.DraftStatus, BadgeType> = {
  NOT_SUBMITTED: { class: 'todo', label: 'To do' },
  REJECTED: { class: 'todo', label: 'To do' },
  IN_REVIEW: { class: 'inReview', label: 'in Review' },
  SUBMITTED: { class: 'waitingForReview', label: 'Waiting for Review' },
  APPROVED: { class: 'approved', label: 'Approved' }
};

function getFileType(url: string) {
  const fileExtension = (url.split('.') as TypeAny).pop().toLowerCase();
  const videoExtensions = ['mp4', 'avi', 'mov', 'webm'];

  if (videoExtensions.includes(fileExtension)) return 'video';

  return 'img';
}

function slideshowOverlay(overlay: OverlayRenderProps, t: TypeAny) {
  if (!overlay.images[overlay.index].src) return null;

  const overlayFile = overlay.images[overlay.index].src as string;

  const overlayFileType = getFileType(overlayFile);

  return (
    <>
      {overlayFileType === 'video' && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={`draft-overlay-${overlay.index}`}
          style={{
            background: 'var(--color-black)',
            display: 'block',
            height: '100%',
            position: 'absolute',
            width: '100%',
            zIndex: 10
          }}
          controls
          playsInline
          poster={getVideoPosterUrl(overlayFile)}
        >
          <source src={overlayFile} type="video/mp4" />
        </video>
      )}

      <div
        style={{
          bottom: '4.5rem',
          padding: '0.5rem',
          position: 'fixed',
          right: 0,
          zIndex: 11
        }}
      >
        <Link href={overlayFile} theme="cta" target="_blank">
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '0.5rem'
            }}
          >
            {t('carousel.download')}
            <Download size={16} />
          </span>
        </Link>
      </div>
    </>
  );
}

export function Draft({ draft, setStatus, setSuccess }: DraftProps) {
  const [expand, setExpand] = useState(() => window.innerWidth > 767);
  const [isEditing, setIsEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [activeMediaId, setActiveMediaId] = useState<string | null>(null);
  const [feedbackPopupItemId, setFeedbackPopupItemId] = useState<string | null>(null);
  const [currentItems, setCurrentItems] = useState<(DraftMedia | FeedbackType)[]>([]);

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedRawFiles, setSelectedRawFiles] = useState<File[]>([]);
  const [form, setForm] = useState<FormType>({
    medias: draft.latest_version?.medias ? [...draft.latest_version.medias] : [],
    caption: draft.latest_version?.caption || '',
    permalink: draft.latest_version?.permalink || ''
  });

  const { progress, isUploading, uploadedCount, uploadFiles } = useUnifiedCloudinaryUpload();

  const { updateDraft, isLoading: isUpdating } = useDraftUpdate(draft.product_id);
  const { submitDraft, isLoading: isSubmitting } = useDraftSubmit({
    draftId: draft.product_id,
    onSuccess: (data) => {
      if ('draft' in data) {
        const key = `influencer-collaboration-${data.draft?.collaboration_id}-${data.draft.beat_id}-drafts`;
        queryClient.setQueryData([key, null], (oldItems: TypeAny) => {
          if (oldItems?.drafts) {
            const res = oldItems.drafts.map((item: Api.Influencer.Draft) =>
              item.product_id === data.draft.product_id ? data.draft : item
            );
            return { drafts: res };
          }
          return { drafts: [] };
        });

        if (['BEAT_FINISHED', 'COLLABORATION_FINISHED'].includes(data.code)) {
          setStatus(data.code);
        } else {
          setSuccess();
        }
      }
    }
  });

  useEffect(() => {
    setForm({
      medias: draft.latest_version?.medias ? [...draft.latest_version.medias] : [],
      caption: draft.latest_version?.caption || '',
      permalink: draft.latest_version?.permalink || ''
    });
  }, [draft]);

  function getMediaIcon(status?: 'NOT_REVIEWED' | 'APPROVED' | 'REJECTED', active?: boolean) {
    if (status === 'APPROVED') {
      return <CheckIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    if (status === 'REJECTED') {
      return <MoreIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    if (status === 'NOT_REVIEWED') {
      return <MoreBlueIcon className={`${styles.feedbackIcon} ${active ? styles.active : ''}`} />;
    }
    return undefined;
  }

  function handleFileRemove(id: string) {
    setForm({
      ...form,
      medias: form.medias.filter((f) => f.cloudinary_id !== id)
    });
  }

  const isFormComplete = () => {
    if (!draft || !Array.isArray(draft.required)) {
      return false;
    }
    let isComplete = true;

    if (draft.required.includes('MEDIAS')) {
      const hasMedias = !!selectedFiles.length || !!form.medias.filter((m) => !m.is_raw).length;
      isComplete = isComplete && hasMedias;
    }

    if (draft.required.includes('RAW_MEDIAS')) {
      const hasRawMedias =
        !!selectedRawFiles.length || !!form.medias.filter((m) => m.is_raw).length;
      isComplete = isComplete && hasRawMedias;
    }

    return isComplete;
  };

  const removeIcon = (filename: string) => (
    <span
      className={styles.removeBtn}
      onClick={(e) => {
        e.stopPropagation();
        handleFileRemove(filename);
      }}
    >
      <Trash2 color="red" size={20} />
    </span>
  );

  const enableEditMode = (resetForm?: boolean) => {
    if (resetForm) {
      setForm({
        ...form,
        caption: '',
        medias: form.medias.filter((m) => m.status === 'APPROVED')
      });
    }
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setForm({
      medias: draft.latest_version?.medias.length ? [...(draft.latest_version?.medias || [])] : [],
      caption: draft.latest_version?.caption || '',
      permalink: draft.latest_version?.permalink || ''
    });
    setSelectedFiles([]);
    setSelectedRawFiles([]);
    setIsEditing(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setForm({
      ...form,
      caption: e.target.value
    });
  };

  const getMediaArray = () => {
    const mediaFiles: FileWithMeta[] = selectedFiles.map((file) => ({
      file,
      isRaw: false
    }));
    const rawMediaFiles: FileWithMeta[] = selectedRawFiles.map((file) => ({
      file,
      isRaw: true
    }));
    return [...mediaFiles, ...rawMediaFiles];
  };

  const draftUpdateCallback = async (files: CloudinaryFile[] | ErrorType, submit?: boolean) => {
    if (!Array.isArray(files)) {
      message.error(t(`alert.${t(files.code)}`));
      return;
    }
    const res = await updateDraft({
      medias: [
        ...form.medias,
        ...files.map(
          (f: CloudinaryFile): DraftDataMedia => ({
            cloudinary_id: f.public_id,
            filename: f.original_filename,
            resource_type: f.resource_type,
            is_raw: f.isRaw
          })
        )
      ],
      caption: form.caption,
      permalink: form.permalink,
      type: !draft.versions.length || draft.status === 'REJECTED' ? 'create' : 'update'
    });
    setSelectedFiles([]);
    setSelectedRawFiles([]);
    setIsEditing(false);
    // setShowHistory(false);
    if (submit && 'code' in res && ['DRAFT_CREATED', 'DRAFT_UPDATED'].includes(res.code)) {
      await submitDraft();
      // message.success(t('alert.SAVED'));
    }
  };

  const handleDraftUpdate = () => {
    // if (!isFormComplete()) {
    //   message.error(t('draft.requiredFields'));
    //   return;
    // }
    uploadFiles(getMediaArray(), draftUpdateCallback);
  };
  const handleSubmitDraft = () => {
    if (!isFormComplete()) {
      message.error(t('draft.requiredFields'));
      return;
    }
    uploadFiles(getMediaArray(), (files) => draftUpdateCallback(files, true));
  };

  const handleMouseEnter = (mediaId: string) => {
    if (isEditing) return;
    setActiveMediaId(mediaId);
  };

  const handleMouseLeave = () => {
    setActiveMediaId(null);
  };

  const mediaItems: (DraftMedia | FeedbackType)[] = [
    ...(draft.latest_version?.medias.filter(
      (m) => m.feedback_client?.trim() || m.feedback_cm?.trim()
    ) || []),
    ...(draft.latest_version?.feedback_cm?.trim() || draft.latest_version?.feedback_client?.trim()
      ? [
          {
            type: 'text',
            media_id: 'general',
            status: (draft.latest_version?.date_approved
              ? 'APPROVED'
              : draft.latest_version?.date_rejected
                ? 'REJECTED'
                : 'NOT_REVIEWED') as 'REJECTED' | 'APPROVED' | 'NOT_REVIEWED',
            feedback_cm: draft.latest_version?.feedback_cm,
            feedback_client: draft.latest_version?.feedback_client
          }
        ]
      : []),
    ...(draft.latest_version?.captions[0]?.feedback_cm?.trim() ||
    draft.latest_version?.captions[0]?.feedback_client?.trim()
      ? [
          {
            type: 'text',
            media_id: 'feedback',
            title: `${processField(draft.product_type).charAt(0).toUpperCase()}${processField(draft.product_type).slice(1)} ${t('draft.caption')}`,
            status: draft.latest_version?.captions[0].status,
            caption: draft.latest_version?.captions[0].caption,
            feedback_cm: draft.latest_version?.captions[0].feedback_cm,
            feedback_client: draft.latest_version?.captions[0].feedback_client
          }
        ]
      : [])
  ];

  const handleFeedbackClick = (initial: string) => {
    setCurrentItems(mediaItems);
    setFeedbackPopupItemId(initial);
  };

  return (
    <div className={`${styles.draftWrap} ${expand ? styles.expand : ''}`}>
      <div className={styles.header}>
        <div className={styles.headerName}>
          <span>{draft.product_name}</span>
          {draft.status === 'REJECTED' && (
            <span className={`${styles.adjustment} ${styles.sm}`}>
              🔴 {t('draft.adjustmentRequired')}
            </span>
          )}
        </div>
        <div className={styles.headerStatus}>
          {draft.status === 'REJECTED' && (
            <span className={styles.adjustment}>🔴 {t('draft.adjustmentRequired')}</span>
          )}
          <span className={`${styles.badge} ${styles[badgeStyles[draft.status].class]}`}>
            {badgeStyles[draft.status].label}
          </span>

          <span className={styles.headerStatusArrow} onClick={() => setExpand(!expand)}>
            {expand ? (
              <ChevronUp size={20} color="#474E5450" />
            ) : (
              <ChevronDown size={20} color="#474E5450" />
            )}
          </span>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.data}>
            {draft.latest_version && (
              <div className={styles.submitted}>
                {`${t('draft.dateSubmitted')}: ${dayjs(draft.latest_version.date_submitted).format('HH:mm, DD.MM.YY')}`}
              </div>
            )}

            {draft.required.includes('MEDIAS') && (
              <div className={styles.dataRow}>
                <div className={styles.rowInfo}>
                  <div className={styles.infoTitle}>
                    {t('draft.finalTitle')} <span>*</span>
                  </div>
                  <div className={styles.infoDescription}>
                    {t('draft.finalSubtitle')
                      .split('\n')
                      .map((line) => (
                        <React.Fragment key={line}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                  </div>
                </div>
                <div className={styles.rowContent}>
                  <PhotoProvider overlayRender={(overlay) => slideshowOverlay(overlay, t)}>
                    {!!form.medias.length &&
                      form.medias
                        .filter((media) => !media.is_raw)
                        .map((m) => {
                          const mediaItem = (
                            <MediaItem
                              key={m.url}
                              src={getMediaThumbnailUrl(m)}
                              topIcon={() =>
                                getMediaIcon(
                                  m.status !== 'NOT_REVIEWED' ? m.status : undefined,
                                  m.media_id === activeMediaId
                                )
                              }
                              bottomIcon={() =>
                                draft.status === 'NOT_SUBMITTED' || isEditing
                                  ? removeIcon(m.cloudinary_id || '')
                                  : undefined
                              }
                            />
                          );

                          // Only wrap in PhotoView if not in edit mode
                          if (draft.status === 'NOT_SUBMITTED' || isEditing) {
                            return mediaItem;
                          }

                          return (
                            <PhotoView key={m.url} src={m.url}>
                              {mediaItem}
                            </PhotoView>
                          );
                        })}
                  </PhotoProvider>
                  {(draft.status === 'NOT_SUBMITTED' || isEditing) && (
                    <FileUploader
                      files={selectedFiles}
                      onFilesChange={(filesData) => setSelectedFiles(filesData)}
                      // isLoading={isLoading || isSubmitting}
                    />
                  )}
                </div>
              </div>
            )}
            <div
              className={styles.dataRow}
              style={{
                display:
                  ['IN_REVIEW', 'APPROVED'].includes(draft.status) &&
                  !draft.latest_version?.captions[0]?.caption
                    ? 'none'
                    : 'block'
              }}
            >
              <div className={styles.rowInfo}>
                <div
                  className={styles.infoTitle}
                >{`${processField(draft.product_type)} ${t('draft.caption')}`}</div>
                <div className={styles.infoDescription}>
                  <Trans
                    className={styles.loginInfoSubtitle}
                    i18nKey="draft.captionSubtitle"
                    components={{
                      1: <span />
                    }}
                    values={{ type: processField(draft.product_type) }}
                  />
                </div>
              </div>
              <div className={styles.rowContent}>
                {draft.status === 'NOT_SUBMITTED' || isEditing ? (
                  <TextAreaAntd
                    value={form.caption}
                    placeholder={t('draft.captionPlaceholder')}
                    // placeholder={`${processField(draft.product_type).charAt(0).toUpperCase()}${processField(draft.product_type).slice(1)} ${t('draft.text')}`}
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    onChange={handleFormChange}
                  />
                ) : (
                  <p
                    className={`${styles.caption} ${
                      draft.latest_version?.captions[0] &&
                      draft.latest_version?.captions[0].status !== 'NOT_REVIEWED'
                        ? styles.spacing
                        : ''
                    } ${activeMediaId === 'feedback' ? styles.active : ''}`}
                  >
                    {draft.latest_version && draft.latest_version?.captions[0] && (
                      <>
                        {getMediaIcon(
                          draft.latest_version.captions[0].status !== 'NOT_REVIEWED'
                            ? draft.latest_version.captions[0].status
                            : undefined
                        )}
                        {draft.latest_version?.captions[0].caption}
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className={styles.dataRow}>
              <div className={styles.rowInfo}>
                <div className={styles.infoTitle}>
                  {t('draft.rawTitle')} <span>*</span>
                </div>
                <div className={styles.infoDescription}>{t('draft.rawSubtitle')}</div>
              </div>
              <div className={styles.rowContent}>
                <PhotoProvider overlayRender={(overlay) => slideshowOverlay(overlay, t)}>
                  {!!form.medias.length &&
                    form.medias
                      .filter((media) => !!media.is_raw)
                      .map((m) => {
                        const mediaItem = (
                          <MediaItem
                            sizeSm
                            key={m.url}
                            src={getMediaThumbnailUrl(m)}
                            topIcon={() =>
                              getMediaIcon(
                                m.status !== 'NOT_REVIEWED' ? m.status : undefined,
                                m.media_id === activeMediaId
                              )
                            }
                            bottomIcon={() =>
                              draft.status === 'NOT_SUBMITTED' || isEditing
                                ? removeIcon(m.cloudinary_id || '')
                                : undefined
                            }
                          />
                        );

                        // Only wrap in PhotoView if not in edit mode
                        if (draft.status === 'NOT_SUBMITTED' || isEditing) {
                          return mediaItem;
                        }

                        return (
                          <PhotoView key={m.url} src={m.url}>
                            {mediaItem}
                          </PhotoView>
                        );
                      })}
                </PhotoProvider>
                {(draft.status === 'NOT_SUBMITTED' || isEditing) && (
                  <FileUploader
                    sizeSm
                    files={selectedRawFiles}
                    onFilesChange={(filesData) => setSelectedRawFiles(filesData)}
                    // isLoading={isLoading || isSubmitting}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.info}>
            {['REJECTED', 'APPROVED'].includes(draft.status) && (
              <div className={styles.infoInner}>
                {((draft.latest_version?.feedback_cm && draft.latest_version.feedback_cm.trim()) ||
                  (draft.latest_version?.feedback_client &&
                    draft.latest_version.feedback_client.trim())) && (
                  <div className={styles.feedback} onClick={() => handleFeedbackClick('general')}>
                    <div className={styles.feedbackTitle}>
                      {getMediaIcon(
                        draft.latest_version?.date_approved
                          ? 'APPROVED'
                          : draft.latest_version?.date_rejected
                            ? 'REJECTED'
                            : 'NOT_REVIEWED'
                      )}
                      {draft.latest_version?.date_approved && (
                        <span className={`${styles.feedbackStatus} ${styles.APPROVED}`}>
                          APPROVED
                        </span>
                      )}
                      {draft.latest_version?.date_rejected && (
                        <span className={`${styles.feedbackStatus} ${styles.REJECTED}`}>
                          REJECTED
                        </span>
                      )}
                      <span>General</span>
                    </div>
                    <div className={styles.feedbackBody}>
                      {draft.latest_version?.feedback_cm &&
                        draft.latest_version?.feedback_cm?.trim() && (
                          <>
                            <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                            {draft.latest_version.feedback_cm}
                          </>
                        )}
                      {draft.latest_version?.feedback_client &&
                        draft.latest_version?.feedback_client?.trim() && (
                          <>
                            <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                            {draft.latest_version.feedback_client}
                          </>
                        )}
                    </div>
                  </div>
                )}
                {((draft.latest_version?.captions[0]?.feedback_cm &&
                  draft.latest_version.captions[0].feedback_cm.trim()) ||
                  (draft.latest_version?.captions[0]?.feedback_client &&
                    draft.latest_version.captions[0].feedback_client.trim())) && (
                  <div
                    className={styles.feedback}
                    onMouseEnter={() => handleMouseEnter('feedback')}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleFeedbackClick('feedback')}
                  >
                    <div className={styles.feedbackTitle}>
                      {getMediaIcon(draft.latest_version?.captions[0].status)}
                      {draft.latest_version?.captions[0].status !== 'NOT_REVIEWED' && (
                        <span
                          className={`${styles.feedbackStatus} ${styles[draft.latest_version?.captions[0].status]}`}
                        >
                          {draft.latest_version?.captions[0].status}
                        </span>
                      )}
                    </div>
                    <div className={styles.feedbackBody}>
                      {draft.latest_version?.captions[0].feedback_cm &&
                        draft.latest_version.captions[0].feedback_cm?.trim() && (
                          <>
                            <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                            {draft.latest_version.captions[0].feedback_cm}
                          </>
                        )}
                      {draft.latest_version?.captions[0].feedback_client &&
                        draft.latest_version.captions[0].feedback_client?.trim() && (
                          <>
                            <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                            {draft.latest_version.captions[0].feedback_client}
                          </>
                        )}
                    </div>
                    {/* <div className={styles.viewMore}>View more</div> */}
                  </div>
                )}
                {draft.latest_version?.medias
                  .filter(
                    (media) =>
                      (media.feedback_cm && media.feedback_cm.trim()) ||
                      (media.feedback_client && media.feedback_client.trim())
                  )
                  .map((m) => (
                    <div
                      key={m.media_id}
                      className={styles.feedback}
                      onMouseEnter={() => handleMouseEnter(m.media_id)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => handleFeedbackClick(m.media_id)}
                    >
                      <div className={styles.feedbackTitle}>
                        {getMediaIcon(m.status)}
                        {m.status !== 'NOT_REVIEWED' && (
                          <span className={`${styles.feedbackStatus} ${styles[m.status]}`}>
                            {m.status}
                          </span>
                        )}
                      </div>
                      {m.feedback_cm && (
                        <div className={styles.feedbackBody}>
                          <span className={styles.feedbackUser}>{t('draft.beatly')}: </span>
                          {m.feedback_cm}
                        </div>
                      )}
                      {m.feedback_client && (
                        <div className={styles.feedbackBody}>
                          <span className={styles.feedbackUser}>{t('draft.client')}: </span>
                          {m.feedback_client}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <div>
            {['APPROVED', 'REJECTED', 'SUBMITTED'].includes(draft.status) &&
              draft.versions.length > 1 && (
                <div className={styles.link} onClick={() => setShowHistory(true)}>
                  {t('draft.showHistory')}
                </div>
              )}
          </div>
          <div className={styles.flex}>
            {isEditing && (
              <>
                <div className={styles.link} onClick={handleCancelEdit}>
                  {t('actions.cancel')}
                </div>
                <Button disabled={isUpdating || isSubmitting} onClick={handleDraftUpdate}>
                  {t('actions.save')}
                </Button>
                <Button
                  // isLoading={isUploading}
                  theme="cta"
                  disabled={isUpdating || isSubmitting}
                  onClick={handleSubmitDraft}
                >
                  {t('actions.saveSubmit')}
                </Button>
              </>
            )}
            {draft.status === 'NOT_SUBMITTED' && (
              <>
                <Button disabled={isUpdating} onClick={handleDraftUpdate}>
                  {t('actions.save')}
                </Button>
                <Button
                  // isLoading={isUploading}
                  theme="cta"
                  disabled={isUpdating}
                  onClick={handleSubmitDraft}
                >
                  {t('actions.saveSubmit')}
                </Button>
              </>
            )}
            {draft.status === 'SUBMITTED' && !isEditing && (
              <Button onClick={() => enableEditMode()}>{t('actions.edit')}</Button>
            )}
            {draft.status === 'REJECTED' && !isEditing && (
              <>
                <Button onClick={() => enableEditMode(true)}>{t('actions.startFresh')}</Button>
                <Button theme="cta" onClick={() => enableEditMode()}>
                  {t('actions.update')}
                </Button>
              </>
            )}
          </div>
        </div>
        {isUploading && (
          <div className={styles.loaderWrapper}>
            <p className={styles.loaderInfo}>
              {`${uploadedCount} / ${selectedFiles.length + selectedRawFiles.length} ${t('draft.filesComplete')}`}
            </p>
            <div className={styles.loaderProgress}>
              <Progress
                percent={progress}
                showInfo={false}
                status="active"
                strokeColor={{
                  '0%': '#7AD098',
                  '100%': '#87d068'
                }}
                size={{ height: 14 }}
              />
            </div>
            <p className={styles.loaderTitle}>{t('draft.uploadTitle')}</p>
            <p className={styles.loaderDescription}>{t('draft.uploadSubtitle')}</p>
          </div>
        )}
      </div>
      {showHistory && (
        <Drawer
          full
          theme="white"
          title={`${draft.product_name} ${t('draft.history')}`}
          onClose={() => setShowHistory(false)}
        >
          {[...draft.versions]
            .reverse()
            .map((v, i) =>
              i ? <DraftHistoryItem key={v.version_id} data={v} type={draft.product_type} /> : null
            )}
        </Drawer>
      )}
      {['REJECTED', 'APPROVED'].includes(draft.status) && !!feedbackPopupItemId && (
        <DraftFeedbackModal
          items={currentItems}
          initialItem={feedbackPopupItemId}
          onClose={() => {
            setFeedbackPopupItemId(null);
            setCurrentItems([]);
          }}
        />
      )}
    </div>
  );
}
